/* Pending by User — scope-aware pendency board (service + country rights). */
import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Download, Loader2, Users } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { API, getAuthHeader } from "@/App";
import { ServiceReportDialog } from "@/components/PendingServiceReport";

const ymd = (d) => d.toISOString().slice(0, 10);
const range = (key, today) => {
  const t = new Date(today + "T00:00:00Z");
  const ago = (n) => { const x = new Date(t); x.setUTCDate(x.getUTCDate() - n); return ymd(x); };
  return { today: [ago(0), ago(0)], d1: [ago(1), ago(1)], d2: [ago(2), ago(2)], d3: [ago(3), ago(3)],
           d4_7: [ago(7), ago(4)], d8_15: [ago(15), ago(8)], d15p: ["", ago(16)], total: ["", ""], urgent: ["", ""] }[key];
};

const Num = ({ v, onClick, cls = "", testId }) => (
  v ? <button type="button" onClick={onClick} data-testid={testId}
              className={`min-w-[28px] px-1.5 py-0.5 rounded-md font-bold hover:ring-2 hover:ring-sky-300 transition-shadow ${cls}`}>{v}</button>
    : <span className="text-slate-300">0</span>);

export const PendingByUserButton = ({ onShow }) => {
  const [open, setOpen] = useState(false);
  const [d, setD] = useState(null);
  const load = () => {
    setD(null);
    axios.get(`${API}/order-register/pending-by-user`, { headers: getAuthHeader() })
      .then(r => setD(r.data)).catch(e => { toast.error(e.response?.data?.detail || "Could not load"); setOpen(false); });
  };
  const excel = async () => {
    const r = await axios.get(`${API}/order-register/pending-by-user/excel`, { headers: getAuthHeader(), responseType: "blob" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(r.data); a.download = `Pending_by_user_${d?.today || ""}.xlsx`; a.click();
  };
  const [svc, setSvc] = useState(null);  // {age, user, label, show} → service-wise window
  const drill = (row, key) => {
    const [from, to] = range(key, d.today);
    setSvc({ age: key, user: row.all ? "" : (row.user_id || ""), label: row.name,
             show: { user_id: row.all ? "" : row.user_id, user_name: row.name, date_from: from, date_to: to, urgent: key === "urgent" } });
  };
  const tot = d?.rows?.[0];
  return (
    <>
      <Button size="sm" variant="outline" onClick={() => { setOpen(true); load(); }} data-testid="pending-by-user-btn"
              className="h-8 text-xs border-amber-300 text-amber-800 hover:bg-amber-50">
        <Users className="w-3.5 h-3.5 mr-1" />Pending by User
      </Button>
      <ServiceReportDialog open={!!svc} setOpen={(v) => { if (!v) setSvc(null); }}
        filter={svc && { age: svc.age, user: svc.user, label: svc.label }}
        onOpenInRegister={(b) => { onShow({ ...svc.show, bucket: b }); setSvc(null); setOpen(false); }} />
      <Dialog open={open && !svc} onOpenChange={setOpen}>
        <DialogContent className="max-w-[96vw] w-[1200px] max-h-[88vh] overflow-y-auto" data-testid="pending-by-user-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <Users className="w-4 h-4 text-amber-600" />Pending by User
              <span className="text-xs font-normal text-slate-500">— by each user's service + country rights · click a number to open it in the table</span>
              {d && <Button size="sm" variant="outline" className="ml-auto h-7 text-xs mr-6" onClick={excel} data-testid="pending-by-user-excel"><Download className="w-3.5 h-3.5 mr-1" />Excel</Button>}
            </DialogTitle>
          </DialogHeader>
          {!d ? <div className="p-8 text-center"><Loader2 className="w-5 h-5 animate-spin inline" /></div> : (<>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 text-center" data-testid="pending-by-user-kpis">
              {[["Urgent ready", tot.urgent, "text-red-600"], ["Pending yesterday", tot.d1, "text-amber-700"], ["Total pending", tot.total, "text-slate-800"],
                ["Released today", tot.released_today, "text-emerald-700"], ["Released this week", tot.released_week, "text-emerald-700"]].map(([l, v, c]) => (
                <div key={l} className="rounded-lg border border-slate-200 bg-slate-50 py-2">
                  <div className={`text-xl font-black ${c}`}>{v}</div><div className="text-[10px] uppercase tracking-wide text-slate-500">{l}</div>
                </div>))}
            </div>
            <div className="overflow-x-auto mt-3">
              <table className="w-full text-xs border-collapse min-w-[980px]" data-testid="pending-by-user-table">
                <thead><tr className="bg-[#DDEBF7]">
                  {["User", "Urgent", ...d.buckets.map(b => b.label), "Total", "Oldest", "Released today", "This week"].map(h => (
                    <th key={h} className={`px-2 py-1.5 border border-slate-200 ${h === "User" ? "text-left" : ""}`}>{h}</th>))}
                </tr></thead>
                <tbody>
                  {d.rows.map((r, i) => (
                    <tr key={r.user_id || "all"} className={i === 0 ? "bg-slate-50 font-semibold" : "hover:bg-amber-50/40"} data-testid={`pending-user-row-${r.user_id || "all"}`}>
                      <td className="px-2 py-1.5 border border-slate-200">
                        <div className="text-slate-800">{r.name}</div>
                        <div className="text-[10px] text-slate-400 truncate max-w-[260px]" title={r.scope}>{r.scope}</div>
                      </td>
                      <td className="px-2 py-1.5 border border-slate-200 text-center"><Num v={r.urgent} cls="bg-red-600 text-white" onClick={() => drill(r, "urgent")} testId={`pbu-${r.user_id || "all"}-urgent`} /></td>
                      {d.buckets.map(b => (
                        <td key={b.key} className="px-2 py-1.5 border border-slate-200 text-center">
                          <Num v={r[b.key]} onClick={() => drill(r, b.key)} testId={`pbu-${r.user_id || "all"}-${b.key}`}
                               cls={b.key === "d15p" || b.key === "d8_15" ? "bg-red-50 text-red-700" : b.key === "d1" ? "bg-amber-100 text-amber-800" : "bg-slate-100 text-slate-700"} />
                        </td>))}
                      <td className="px-2 py-1.5 border border-slate-200 text-center"><Num v={r.total} cls="bg-slate-800 text-white" onClick={() => drill(r, "total")} testId={`pbu-${r.user_id || "all"}-total`} /></td>
                      <td className="px-2 py-1.5 border border-slate-200 text-center text-slate-500">{r.oldest ? r.oldest.split("-").reverse().join("/") : "—"}</td>
                      <td className="px-2 py-1.5 border border-slate-200 text-center font-bold text-emerald-700">{r.released_today || 0}</td>
                      <td className="px-2 py-1.5 border border-slate-200 text-center font-bold text-emerald-700">{r.released_week || 0}</td>
                    </tr>))}
                </tbody>
              </table>
            </div>
            <p className="text-[10px] text-slate-400 mt-2">Pending = not cancelled and not on a completed invoice. Buckets use the order date. Released = parcels on invoices completed today / since Monday.</p>
          </>)}
        </DialogContent>
      </Dialog>
    </>
  );
};
